/**
 * Onboarding Store
 * Zustand store for managing AI onboarding flow state
 */

import create from 'zustand';
import { aiApi } from '../api/endpoints/ai';
import type { OnboardingStep, OnboardingStepData, DiagnosticData } from '../api/types';
import logger from '../utils/logger';

interface OnboardingState {
  currentStep: OnboardingStep | null;
  diagnosticData: DiagnosticData | null;
  pendingNavigationAction: string | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchStatus: () => Promise<void>;
  submitStep: (step: string, data: OnboardingStepData) => Promise<void>;
  submitDiagnosticAction: (action: string) => Promise<{ action: string; conversation_id: number }>;
  consumePendingNavigationAction: () => string | null;
  reset: () => void;
  clearError: () => void;
}

export const useOnboardingStore = create<OnboardingState>((set, get) => ({
  currentStep: null,
  diagnosticData: null,
  pendingNavigationAction: null,
  isLoading: false,
  error: null,

  fetchStatus: async () => {
    set({ isLoading: true, error: null });
    try {
      const status = await aiApi.getOnboardingStatus();
      logger.log('[onboardingStore.fetchStatus] Status:', status);
      set({
        currentStep: status,
        diagnosticData: status.diagnostic_data || null,
        isLoading: false,
      });
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errors
        ? Object.values(error.response.data.errors).flat().join(', ')
        : error.message || 'Failed to fetch onboarding status';
      logger.error('[onboardingStore.fetchStatus] Error:', errorMessage);
      set({ error: errorMessage, isLoading: false });
    }
  },

  submitStep: async (step: string, data: OnboardingStepData) => {
    set({ isLoading: true, error: null });
    try {
      logger.log('[onboardingStore.submitStep] Submitting:', { step, data });
      const response = await aiApi.submitOnboardingStep(step, data);
      logger.log('[onboardingStore.submitStep] Response:', response);

      // Store diagnostic data if present in response
      const diagnosticData = response.diagnostic_data || null;

      set({
        currentStep: {
          step: response.step,
          completed: response.completed,
          message: response.message,
          profile: get().currentStep?.profile || {},
          diagnostic_data: diagnosticData,
        },
        diagnosticData,
        isLoading: false,
      });
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errors
        ? Object.values(error.response.data.errors).flat().join(', ')
        : error.message || 'Failed to submit onboarding step';
      logger.error('[onboardingStore.submitStep] Error:', errorMessage);
      set({ error: errorMessage, isLoading: false });
    }
  },

  submitDiagnosticAction: async (action: string) => {
    set({ isLoading: true, error: null });
    try {
      logger.log('[onboardingStore.submitDiagnosticAction] Action:', action);
      const response = await aiApi.submitDiagnosticAction(action);
      logger.log('[onboardingStore.submitDiagnosticAction] Response:', response);

      set({
        currentStep: {
          step: 'complete',
          completed: true,
          message: { message: null },
          profile: get().currentStep?.profile || {},
        },
        pendingNavigationAction: response.action,
        isLoading: false,
      });

      return { action: response.action, conversation_id: response.conversation_id };
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errors
        ? Object.values(error.response.data.errors).flat().join(', ')
        : error.message || 'Failed to submit diagnostic action';
      logger.error('[onboardingStore.submitDiagnosticAction] Error:', errorMessage);
      set({ error: errorMessage, isLoading: false });
      throw error;
    }
  },

  consumePendingNavigationAction: () => {
    const action = get().pendingNavigationAction;
    if (action) {
      set({ pendingNavigationAction: null });
    }
    return action;
  },

  reset: () =>
    set({
      currentStep: null,
      diagnosticData: null,
      pendingNavigationAction: null,
      isLoading: false,
      error: null,
    }),

  clearError: () => set({ error: null }),
}));
